<template>
    <skeleton v-if="isLoading" />
    <form
        v-else
        @submit="onSubmit"
    >
        <content-reset :body="formMarkup" />

        <div
            v-if="activeFacilities !== null && activeFacilities.length > 1"
            class="mt-6 flex flex-col"
        >
            <label
                for="select_facility"
                class="font-semibold"
            >
                Facility
            </label>
            <span class="w-full">
                <prime-dropdown
                    id="select_facility"
                    v-model="facilityId"
                    :options="activeFacilities"
                    option-label="label"
                    option-value="id"
                />
            </span>
            <span
                id="facility-error"
                class="text-small text-red-600"
            >
                {{ errors.facilityId || '&nbsp;' }}
            </span>
        </div>

        <div class="mt-2">
            <label class="font-semibold">Customer</label>
            <span class="w-full">
                <input-text
                    :model-value="viewer?.name"
                    disabled
                    class="w-full"
                    placeholder="Subject"
                    maxlength="255"
                />
            </span>
        </div>

        <div class="mt-6">
            <div>
                <label class="font-semibold">Email Address</label>
                <span
                    v-show="errors.email"
                    id="email-error"
                    class="text-small text-red-600"
                >
                    &mdash; {{ errors.email }}
                </span>
            </div>
            <span class="p-input-icon-left w-full">
                <i class="pi pi-envelope"></i>
                <input-text
                    id="email"
                    v-model="emailValue"
                    v-bind="emailProps"
                    inputmode="email"
                    type="email"
                    class="w-full !text-[16px]"
                    :pt="{
                        root: {
                            class: errors.email
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                    placeholder="Email Address"
                    aria-describedby="email-error"
                />
            </span>
            <span class="text-sm text-gray-500"
                >We’ll respond to this email address. If you change it, your
                customer record will be updated automatically.</span
            >
        </div>

        <div class="mt-6">
            <div>
                <label class="font-semibold">Subject</label>
                <span
                    v-show="errors.subject"
                    id="email-error"
                    class="text-small text-red-600"
                >
                    &mdash; {{ errors.subject }}
                </span>
            </div>
            <span class="w-full">
                <input-text
                    id="subject"
                    v-model="subject"
                    v-bind="subjectProps"
                    class="w-full !text-[16px]"
                    placeholder="Subject"
                    maxlength="255"
                    autofocus
                    :pt="{
                        root: {
                            class: errors.subject
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
            </span>
        </div>

        <div class="mt-6">
            <div>
                <label class="font-semibold">Message</label>
                <span
                    v-show="errors.message"
                    id="message-error"
                    class="text-small text-red-600"
                >
                    &mdash; {{ errors.message }}
                </span>
            </div>
            <span class="w-full">
                <prime-textarea
                    id="message"
                    v-model="message"
                    v-bind="messageProps"
                    class="w-full"
                    placeholder="Message"
                    :rows="5"
                    :cols="30"
                    maxlength="1000"
                    :pt="{
                        root: {
                            class: errors.message
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
            </span>
        </div>

        <div class="mt-4 flex justify-end">
            <prime-button
                type="submit"
                label="Submit"
                :loading="isPending"
            />
        </div>
    </form>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button'
import InputText from 'primevue/inputtext'
import ContentReset from '~/components/ContentReset.vue'
import PrimeTextarea from 'primevue/textarea/Textarea.vue'
import PrimeDropdown from 'primevue/dropdown'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import { computed, onMounted, toRef, watch } from 'vue'
import { useViewer, useSetViewer } from '~/composables/use-login'
import { toTypedSchema } from '@vee-validate/yup'
import { object, string } from 'yup'
import { useForm } from 'vee-validate'
import { useToast } from 'primevue/usetoast'
import { useCreateTicketMutation } from '~/components/Tickets/use-create-ticket-mutation'
import { TicketConfirmationTicketFragment } from '~/components/Tickets/TicketConfirmation.vue'
import Skeleton from 'primevue/skeleton'

const props = defineProps<{
    formMarkup: string
    // If this props is not provided, a facility selector will be shown
    facilityIdContext?: string
}>()

const emit = defineEmits<{
    (e: 'ticketCreated', payload: TicketConfirmationTicketFragment): void
}>()

const facilityIdContextRef = toRef(props, 'facilityIdContext')

const query = useRpQuery({ orgLevel: true })
// TODO: when there are more than 100 facilities
const { data, isLoading } = useQuery({
    queryKey: ['LoggedInContactUsTicketFormQuery', facilityIdContextRef],
    placeholderData: previousData => previousData,
    queryFn: () => {
        // If a facility has been provided, no need for this query
        if (facilityIdContextRef.value !== undefined) {
            return null
        }

        return query(
            graphql(/* GraphQL */ `
                query LoggedInContactUsTicketFormQuery {
                    facilities(first: 100) {
                        edges {
                            node {
                                id
                                shortName
                                active
                            }
                        }
                    }
                }
            `)
        )
    }
})

const activeFacilities = computed(() => {
    // If a facility has been provided, no need for this
    if (props.facilityIdContext !== undefined) {
        return null
    }

    return (
        data.value?.facilities.edges
            .filter(edge => {
                return edge.node.active === true
            })
            .map(edge => ({
                label: edge.node.shortName,
                id: edge.node.id
            })) ?? []
    )
})

const viewer = useViewer()

const schema = toTypedSchema(
    object({
        email: string()
            .email('Please enter a valid email')
            .required('Please enter an email'),
        subject: string().max(255).required('Please enter a subject'),
        message: string().max(1000).required('Please enter a message'),
        facilityId: string().required('Please select a facility')
    })
)

const { errors, defineField, handleSubmit } = useForm({
    validationSchema: schema,
    initialValues: {
        email: viewer.value?.customerEmail ?? ''
    }
})

const [emailValue, emailProps] = defineField('email', {
    validateOnModelUpdate: false,
    validateOnBlur: true
})

const [subject, subjectProps] = defineField('subject', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const [message, messageProps] = defineField('message', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const [facilityId] = defineField('facilityId')

// This is for handling the 1 facility case
watch(activeFacilities, newValue => {
    if (newValue === null) {
        return
    }

    if (newValue.length === 1) {
        facilityId.value = newValue[0].id
    }
})

// This is for handling the case where a facility has been provided
onMounted(() => {
    if (props.facilityIdContext !== undefined) {
        facilityId.value = props.facilityIdContext
    }
})

const toast = useToast()
const setViewer = useSetViewer()
const onSubmit = handleSubmit(values => {
    mutate(
        {
            email: values.email,
            subject: values.subject,
            message: values.message,
            facilityId: values.facilityId
        },
        {
            onSuccess: data => {
                // Update viewer because email has changed
                setViewer(data.query.viewer)
                emit('ticketCreated', data.record)
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Ticket Created',
                    life: 3000,
                    closable: false
                })
            }
        }
    )
})

const { mutate, isPending } = useCreateTicketMutation()
</script>
